import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DateTimeField, ProblemError, hasGrant, usePrincipal } from "@csa/web-foundation";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Select,
} from "../../ui";
import { useT } from "../../i18n/index";
import {
  ASOF_PATTERN,
  QUALITY_METRIC_KEYS,
  QUALITY_METRIC_PATTERN,
  type CarrierQualitySnapshot,
  type CarrierQualityView,
  type CarrierView,
  type CurateCarrierQualityInput,
  type QualityMetricKey,
} from "../../api/catalog-contracts";
import { METRIC_HELP_KEYS, METRIC_LABEL_KEYS } from "./types";
import {
  useCarrierQuality,
  useCarrierQualityHistory,
  useCarriers,
  useCurateQuality,
} from "./use-catalog";

// AL1 / 05 — the carrier-quality scorecard, reproduced from the founder's 05 export with
// the UI-kit (score-ring hero, metric bars with validation-error + cold-start states,
// append-only version history, audit info, and the fixed save bar). No business logic in
// the browser: the form renders what the curation lead types and what the API returns.
//
// STRUCTURALLY TRUTHFUL governance — the REAL invariant on this resource is preserved:
//   • append-only  — every write is a NEW dated CURATED snapshot; latest-by-as_of wins.
//   • audited      — POST …/quality is @Audited (catalog.carrier.quality), catalog:write.
//   • reconstructable — source + as_of + recorded-at are surfaced from the snapshot.
// A1 UPDATE — the composite score, its weights and the full version list now HAVE reads
// (GET …/quality carries the score + component breakdown; GET …/quality/history carries
// the series). They are still computed entirely server-side from the governed
// CARRIER_QUALITY namespace: this file displays them and calculates nothing. A null score
// and an empty history remain real states, rendered as such rather than filled in.
// Separate-approver four-eyes (proposer ≠ approver) is not part of this endpoint (that
// path is the config-governance surface) — flagged, not faked.

export function CarrierQualityScorecard() {
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const carriers = useCarriers();
  const code = params.get("code") ?? "";

  const setCode = (next: string) => {
    const p = new URLSearchParams(params);
    if (next) p.set("code", next);
    else p.delete("code");
    setParams(p, { replace: true });
  };

  return (
    <section className="sc">
      {/* BREADCRUMB + SEÇİCİ */}
      <div className="sc-breadcrumb">
        <span className="muted">{t("sc.breadcrumbRoot")}</span>
        <Icon name="chevron-right" size={14} strokeWidth={2} className="muted" />
        <span className="sc-crumb-current">
          {code
            ? (carriers.data?.data.find((c) => c.code === code)?.current.tradeName ?? code)
            : t("catalog.heading")}
        </span>
        <span className="sc-flex" />
        {carriers.data && carriers.data.data.length > 0 && (
          <Select
            fieldClassName="sc-picker"
            label={t("catalog.pickCarrier")}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          >
            <option value="">—</option>
            {carriers.data.data.map((c) => (
              <option key={c.id} value={c.code}>
                {c.code} — {c.current.tradeName}
              </option>
            ))}
          </Select>
        )}
      </div>

      {carriers.isLoading && <LoadingState label={t("catalog.loadingCarriers")} rows={2} />}
      {carriers.error != null && <ErrorState title={t("catalog.carriersError")} />}
      {carriers.data && carriers.data.data.length === 0 && (
        <EmptyState icon="records" title={t("catalog.noCarriers")} />
      )}

      {code === "" ? (
        <p className="muted">{t("catalog.selectPrompt")}</p>
      ) : (
        <CarrierScorecard
          code={code}
          carrier={carriers.data?.data.find((c) => c.code === code) ?? null}
        />
      )}
    </section>
  );
}

function CarrierScorecard({ code, carrier }: { code: string; carrier: CarrierView | null }) {
  const { t } = useT();
  const { me } = usePrincipal();
  const quality = useCarrierQuality(code);
  const canWrite = hasGrant(me, "catalog:write", "all");
  const snapshot = quality.data ?? null;

  const curate = useCurateQuality(code);
  const [metrics, setMetrics] = useState<Record<QualityMetricKey, string>>({
    claimsPaymentSpeedDays: "",
    claimsAcceptanceRatio: "",
    complaintRatio: "",
    serviceScore: "",
    solvencyRatio: "",
    networkStrength: "",
  });
  const [asOf, setAsOf] = useState("");

  const filled = QUALITY_METRIC_KEYS.filter((k) => metrics[k].trim() !== "");
  const invalidMetric = (k: QualityMetricKey) =>
    metrics[k].trim() !== "" && !QUALITY_METRIC_PATTERN.test(metrics[k].trim());
  const anyMetricInvalid = QUALITY_METRIC_KEYS.some(invalidMetric);
  const asOfInvalid = asOf.trim() !== "" && !ASOF_PATTERN.test(asOf.trim());
  const dirty = filled.length > 0 || asOf.trim() !== "";

  const canSubmit =
    canWrite && filled.length >= 1 && !anyMetricInvalid && !asOfInvalid && !curate.isPending;

  function submit() {
    const body: CurateCarrierQualityInput = {};
    for (const k of QUALITY_METRIC_KEYS) {
      const v = metrics[k].trim();
      if (v) body[k] = v;
    }
    const a = asOf.trim();
    if (a) body.asOf = a;
    curate.mutate(body);
  }

  function discard() {
    setMetrics({
      claimsPaymentSpeedDays: "",
      claimsAcceptanceRatio: "",
      complaintRatio: "",
      serviceScore: "",
      solvencyRatio: "",
      networkStrength: "",
    });
    setAsOf("");
  }

  return (
    <div className="sc-grid">
      <div className="sc-col-left">
        <ScoreHero carrier={carrier} quality={snapshot} dirty={dirty} />

        {/* KALİTE ÖLÇÜTLERİ (BAR KIRILIMI) — the curate form */}
        <Card padding="sm" className="sc-metrics-card">
          <div className="sc-card-head">
            <span className="sc-card-title">{t("sc.metricsTitle")}</span>
          </div>
          <p className="sc-weight-note muted">{t("sc.metricsWeightNote")}</p>

          {quality.isLoading && <LoadingState label={t("quality.loading")} rows={3} />}
          {quality.error instanceof ProblemError && (
            <p role="alert" className="form-error">
              {quality.error.problem.detail}
            </p>
          )}
          {!quality.isLoading && snapshot === null && (
            <div className="sc-coldband" data-testid="quality-coldstart">
              <Icon name="info" size={15} strokeWidth={2} />
              <span>{t("quality.coldStart")}</span>
            </div>
          )}

          {snapshot && (
            <div className="sc-source" data-testid="quality-latest">
              <p>
                <span>
                  {t("quality.source")}: <strong>{snapshot.source}</strong>
                </span>{" "}
                <span className="muted">
                  · {t("quality.asOf")}: {snapshot.asOf}
                </span>
              </p>
              <ul className="sc-latest-vals">
                {QUALITY_METRIC_KEYS.map((k) => (
                  <li key={k}>
                    <span className="muted">{t(METRIC_LABEL_KEYS[k])}:</span> {snapshot[k] ?? "—"}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <form
            className="sc-metric-list"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) submit();
            }}
          >
            <fieldset disabled={!canWrite}>
              {!canWrite && <p className="muted">{t("form.readOnly")}</p>}
              {QUALITY_METRIC_KEYS.map((k) => (
                <MetricRow
                  key={k}
                  metricKey={k}
                  value={metrics[k]}
                  current={snapshot?.[k] ?? null}
                  onChange={(v) => setMetrics((m) => ({ ...m, [k]: v }))}
                  invalid={invalidMetric(k)}
                  hasSnapshot={snapshot !== null}
                />
              ))}

              <DateTimeField
                label={t("form.asOf")}
                value={asOf}
                onChange={setAsOf}
                todayLabel={t("df.today")}
                nowLabel={t("df.now")}
                error={asOfInvalid ? t("form.invalidAsOf") : undefined}
                hint={!asOfInvalid ? t("form.asOfHint") : undefined}
                data-testid="asof"
              />
            </fieldset>

            {canWrite && filled.length === 0 && <p className="muted">{t("form.atLeastOne")}</p>}
          </form>
          {/* the append-only / audit-trail invariant, stated plainly */}
          <p className="sc-appendnote muted">{t("quality.appendOnlyNote")}</p>
        </Card>
      </div>

      <div className="sc-col-right">
        <VersionHistory code={code} />
        <AuditInfo snapshot={snapshot} />
      </div>

      {/* DÖRT-GÖZ / APPEND-ONLY KAYDET ÇUBUĞU (SABİT ALT BAR) */}
      <div className="sc-savebar">
        <div className="sc-savebar-inner">
          {dirty && (
            <span className="sc-unsaved">
              <span className="sc-unsaved-dot" />
              {t("sc.saveUnsaved")}
            </span>
          )}
          <span className="muted sc-savebar-note">
            <Icon name="lock" size={13} strokeWidth={1.9} /> {t("sc.saveGovernNote")}
          </span>
          <span className="sc-flex" />
          {curate.isSuccess && (
            <span
              role="status"
              className="form-ok sc-savebar-status"
              data-testid="sc-savebar-status"
            >
              {t("form.success")}
            </span>
          )}
          {curate.error instanceof ProblemError && (
            <span role="alert" className="form-error sc-savebar-status">
              {curate.error.problem.detail}
            </span>
          )}
          <Button variant="secondary" type="button" onClick={discard} disabled={!dirty}>
            {t("sc.saveDiscard")}
          </Button>
          <Button
            variant="primary"
            type="button"
            leadingIcon={<Icon name="arrow-right" size={16} strokeWidth={2} />}
            onClick={submit}
            disabled={!canSubmit}
            loading={curate.isPending}
          >
            {t("form.curate")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** The founder's score-ring hero.
 *
 *  A1 — the ring now shows the REAL composite. It used to be a permanent "—/100" with a
 *  "backend-first" note, because the six metrics were stored but never combined. The
 *  score is still never computed in the browser: the API derives it from the governed
 *  CARRIER_QUALITY weights and returns it with its component breakdown.
 *
 *  `score: null` is a real state (a snapshot with no usable metric) and still renders
 *  "—". And a LOW weightCoverage is surfaced explicitly: a 92 computed from one of six
 *  metrics is not the same claim as a 92 computed from all six, and a curator steering
 *  households by this number has to be able to tell the difference. */
function ScoreHero({
  carrier,
  quality,
  dirty,
}: {
  carrier: CarrierView | null;
  quality: CarrierQualityView | null;
  dirty: boolean;
}) {
  const { t } = useT();
  const score = quality?.score ?? null;
  // The ring arc: r=15.5 → circumference ≈ 97.39. Rendered only for a real score.
  const arc = score === null ? null : `${((score / 100) * 97.39).toFixed(2)} 97.39`;
  const coverage = quality?.weightCoverage ?? 0;
  const partial = score !== null && coverage < 0.999;

  return (
    <Card padding="md" className="sc-hero">
      <div className="sc-hero-left">
        <div className="sc-ring" aria-hidden="true">
          <svg viewBox="0 0 36 36" className="sc-ring-svg">
            <circle cx="18" cy="18" r="15.5" className="sc-ring-track" />
            {arc && (
              <circle
                cx="18"
                cy="18"
                r="15.5"
                className="sc-ring-progress"
                strokeDasharray={arc}
                transform="rotate(-90 18 18)"
              />
            )}
          </svg>
          <div className="sc-ring-center">
            <span className="sc-ring-value" data-testid="sc-score-value">
              {score === null ? "—" : Math.round(score)}
            </span>
            <span className="sc-ring-outof">{t("sc.scoreOutOf")}</span>
          </div>
        </div>
        <div>
          <div className="sc-hero-eyebrow">{t("sc.scoreTitle")}</div>
          <div className="sc-hero-name">{carrier?.current.tradeName ?? "—"}</div>
          {score === null ? (
            <p className="sc-hero-pending muted" data-testid="sc-score-pending">
              {t("sc.scoreNoMetrics")}
            </p>
          ) : (
            <p className="sc-hero-pending muted" data-testid="sc-score-basis">
              {t("sc.scoreBasis")}: {quality?.metricsUsed.length ?? 0}/6 ·{" "}
              {t("sc.scoreConfigVersion")} v{quality?.configVersion ?? "—"}
            </p>
          )}
          {partial && (
            <p className="sc-hero-pending muted" role="note" data-testid="sc-score-partial">
              {t("sc.scorePartial")}
            </p>
          )}
        </div>
      </div>
      {dirty && (
        <span className="sc-draftbadge" data-testid="sc-draftbadge">
          <Icon name="edit" size={13} strokeWidth={2} />
          {t("sc.draftEditing")}
        </span>
      )}
    </Card>
  );
}

function MetricRow({
  metricKey: k,
  value,
  current,
  onChange,
  invalid,
  hasSnapshot,
}: {
  metricKey: QualityMetricKey;
  value: string;
  current: string | null;
  onChange: (v: string) => void;
  invalid: boolean;
  hasSnapshot: boolean;
}) {
  const { t } = useT();
  // The bar visualises the raw entered/loaded value on a 0–100 reference (a display cue,
  // NOT the governed composite score). Ratio/day metrics simply read low on this scale.
  const shown = value.trim() !== "" ? value.trim() : (current ?? "");
  const num = Number(shown);
  const pct = useMemo(() => {
    if (shown === "" || !Number.isFinite(num)) return 0;
    return Math.max(0, Math.min(100, num));
  }, [shown, num]);
  // cold-start per metric: a carrier with a snapshot but no value for THIS metric.
  const coldMetric = hasSnapshot && current === null && value.trim() === "";

  return (
    <div className={`sc-metric${invalid ? " sc-metric--invalid" : ""}`}>
      <div className="sc-metric-main">
        <Input
          label={t(METRIC_LABEL_KEYS[k])}
          fieldClassName="sc-metric-input"
          aria-label={t(METRIC_LABEL_KEYS[k])}
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          error={invalid ? t("form.invalidMetric") : undefined}
          errorTestId={`err-${k}`}
          placeholder={coldMetric ? "—" : undefined}
        />
        <div className="sc-metric-bar">
          <div className="sc-metric-track" aria-hidden="true">
            <div
              className={`sc-metric-fill${invalid ? " sc-metric-fill--invalid" : ""}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="sc-metric-cur muted">
            {coldMetric ? t("sc.metricColdStart") : shown !== "" ? shown : "—"}
          </div>
        </div>
      </div>
      <span className="muted sc-metric-help" data-testid={`help-${k}`}>
        {t(METRIC_HELP_KEYS[k])}
      </span>
    </div>
  );
}

/** A1 — the append-only version history, now a REAL series.
 *
 *  `carrier_quality_snapshot` has been append-only since ADR 0004 §3, but the only read
 *  exposed was latest-by-as_of with .limit(1), so this panel could physically hold ONE
 *  row — the trend the append-only design exists to make visible was unreachable through
 *  the product. GET …/quality/history now returns the series, each row with its own
 *  composite, so a curator can see the score MOVE and not just its latest value. */
function VersionHistory({ code }: { code: string | null }) {
  const { t } = useT();
  const history = useCarrierQualityHistory(code);
  const rows = history.data?.data ?? [];

  return (
    <Card padding="sm" className="sc-history">
      <div className="sc-card-head">
        <span className="sc-card-title">{t("sc.historyTitle")}</span>
        <Badge variant="standard">{t("sc.historyAppendOnly")}</Badge>
      </div>
      {history.isLoading && <LoadingState label={t("quality.loading")} rows={2} />}
      {/* "Bu kurum hiç puanlanmadı" is a claim about the carrier; a failed read
          is a claim about the request. They were the same screen. */}
      {!history.isLoading && history.error != null && (
        <ErrorState title={t("sc.historyLoadFailed")} />
      )}
      {!history.isLoading && history.error == null && rows.length === 0 && (
        <EmptyState icon="records" title={t("sc.historyNone")} />
      )}
      {rows.map((r, i) => (
        <div className="sc-history-row" key={r.id} data-testid="sc-history-row">
          <span>
            {i === 0 ? `${t("sc.historyCurrent")} · ` : ""}
            {r.asOf}
          </span>
          <span className="sc-history-score">{r.score === null ? "—" : Math.round(r.score)}</span>
          <Badge variant={i === 0 ? "success" : "standard"}>{r.source}</Badge>
        </div>
      ))}
    </Card>
  );
}

/** Audit info (reconstructability). Source + as_of + recorded-at come straight from the
 *  snapshot. The append-only/audited invariant and the four-eyes-is-backend-first caveat
 *  are stated honestly — no proposer/approver identities are invented. */
function AuditInfo({ snapshot }: { snapshot: CarrierQualitySnapshot | null }) {
  const { t } = useT();
  return (
    <Card padding="sm" className="sc-audit">
      <div className="sc-card-head">
        <span className="sc-card-title">{t("sc.auditTitle")}</span>
      </div>
      <dl className="sc-audit-list">
        <div>
          <dt className="muted">{t("sc.auditSource")}</dt>
          <dd>{snapshot ? snapshot.source : "—"}</dd>
        </div>
        <div>
          <dt className="muted">{t("sc.auditAsOf")}</dt>
          <dd>{snapshot ? snapshot.asOf : "—"}</dd>
        </div>
        <div>
          <dt className="muted">{t("sc.auditRecorded")}</dt>
          <dd>{snapshot ? snapshot.createdAt : "—"}</dd>
        </div>
      </dl>
      <p className="sc-appendnote muted">{t("sc.auditGovernNote")}</p>
      <p className="sc-appendnote muted">{t("sc.auditFourEyesBackendFirst")}</p>
    </Card>
  );
}
